// The reel flow's rules, with no React: one page, one object (the reel on its stage), and one
// thing to do at a time. The step is never kept in state; it is read off what the server holds,
// so a reload or a return from Posts lands where the reel actually is.
import {
  CREDIT_COST,
  type PostTemplate,
  type ReelJobDto,
  type ReelShot,
  type TextLayer,
} from '@str/shared';

export type Phase =
  'moment' | 'building' | 'board' | 'filming' | 'words' | 'music' | 'mixing' | 'share';

export type PhaseInput = {
  /** The post, with the versions it has loaded so far. */
  post: { id: string; versions?: { id: string }[] } | null;
  jobs: ReelJobDto[];
  /** The post's current accepted version, when it has one. */
  version: { videoUrl: string | null; videoEdit: { audio?: unknown } | null } | null;
  /** A render laying the words and the track onto the reel is in flight. */
  mixing: boolean;
  /** The host chose to keep the reel silent. */
  silent: boolean;
  /** The host went back to change the idea. */
  editing: boolean;
  /** The host has settled the words on the reel (kept some, or none). */
  words?: boolean;
};

const working = (j: ReelJobDto) => j.state === 'pending' || j.state === 'running';

/** The newest complete storyboard, the one the film is made from. */
export function boardOf(jobs: ReelJobDto[]): ReelJobDto | null {
  return jobs.filter((j) => j.kind === 'storyboard' && j.state === 'complete').at(-1) ?? null;
}

export function phaseOf({
  post,
  jobs,
  version,
  mixing,
  silent,
  editing,
  words = false,
}: PhaseInput): Phase {
  if (editing || !post) return 'moment';
  const running = jobs.find(working);
  if (running) return running.kind === 'storyboard' ? 'building' : 'filming';
  // The job poll lands before the post refetch: a finished film whose version the page has not
  // loaded yet is still filming, never the board with its paid "Film it" button again.
  const film = jobs.filter((j) => j.kind === 'video').at(-1);
  if (
    film?.state === 'complete' &&
    film.versionId &&
    !post.versions?.some((v) => v.id === film.versionId)
  )
    return 'filming';
  if (mixing) return 'mixing';
  if (version?.videoUrl) {
    // A render (words, music, or both) or the host's "keep it silent" finishes the reel.
    if (version.videoEdit || silent) return 'share';
    return words ? 'music' : 'words';
  }
  return boardOf(jobs) ? 'board' : 'moment';
}

/* ------------------------------------------------- the real steps a job is at */

export type FlowStep = { label: string; status: 'done' | 'doing' | 'todo' };

function ladder(labels: string[], at: number, doneAll: boolean): FlowStep[] {
  return labels.map((label, i) => ({
    label,
    status: doneAll || i < at ? 'done' : i === at ? 'doing' : 'todo',
  }));
}

/** "scene 3" or "scenes 2 and 5": the scenes a redo names, in words. */
export function sceneList(scenes: number[]): string {
  if (scenes.length === 1) return `scene ${scenes[0]}`;
  return `scenes ${scenes.slice(0, -1).join(', ')} and ${scenes.at(-1)}`;
}

/** Read from the storyboard service's own status and progress line; no invented percentages. */
export function storyboardSteps(job: ReelJobDto | null): FlowStep[] {
  // A redo of named scenes has no story to plan: it draws them again and checks each frame.
  if (job?.input.kind === 'storyboard' && job.input.scenes?.length) {
    const labels = [`Drawing ${sceneList(job.input.scenes)} again`, 'Checking every frame'];
    if (job.state === 'complete') return ladder(labels, 0, true);
    const progress = job.result?.progress ?? '';
    return ladder(labels, /Checking/i.test(progress) ? 1 : 0, false);
  }
  const labels = [
    'Reading your photos',
    'Planning the story',
    'Drawing the scenes',
    'Checking every frame',
  ];
  if (!job || job.state === 'complete') return ladder(labels, 0, !!job);
  const r = job.result;
  if (!r || r.status === 'planning') return ladder(labels, r ? 1 : 0, false);
  const progress = r.progress ?? '';
  const drawing = /scene (\d+) of (\d+):?\s*(.*)$/i.exec(progress);
  const checking = /Checking scene (\d+)/i.exec(progress);
  const total = drawing?.[2] ?? String(r.shots?.length ?? 6);
  if (checking) labels[2] = `Checking scene ${checking[1]} of ${total}`;
  else if (drawing)
    labels[2] = `Drawing scene ${drawing[1]} of ${drawing[2]}${drawing[3] ? ` · ${drawing[3]}` : ''}`;
  return ladder(labels, 2, false);
}

/** Shots finished, read off the job's "N of M shots filmed" line; null when the line does not say. */
export function shotsFilmed(job: ReelJobDto | null): number | null {
  const filmed = /(\d+) of (\d+) shots filmed/i.exec(job?.result?.progress ?? '');
  return filmed ? Number(filmed[1]) : null;
}

/**
 * The most shots this take has been seen to finish. Lines naming the shot being sent or checked
 * carry no count, and shots film side by side, so the count shown is the highest one read (J14).
 */
export function keepFilmed(
  prev: { id: string; filmed: number } | null,
  job: ReelJobDto | null,
): { id: string; filmed: number } | null {
  if (!job) return null;
  const before = prev?.id === job.id ? prev.filmed : 0;
  return { id: job.id, filmed: Math.max(before, shotsFilmed(job) ?? 0) };
}

/**
 * The filming ladder. `filmed` is the highest finished count seen for this take (keepFilmed) and
 * `shots` the board's shot count, for lines that do not name it. Shots finished and the shot being
 * worked on are kept apart: "2 of 4 shots filmed · now on shot 3", never "shot 3 of 4".
 */
export function filmSteps(job: ReelJobDto | null, filmed = 0, shots = 0): FlowStep[] {
  const labels = ['Checking the scenes', 'Filming', 'Cutting it together'];
  if (!job || job.state === 'complete') return ladder(labels, 0, !!job);
  const progress = job.result?.progress ?? '';
  if (/assembl|ready/i.test(progress)) return ladder(labels, 2, false);
  // Seedance films each shot as its own call, a few at a time.
  const count = /(\d+) of (\d+) shots filmed/i.exec(progress);
  const shot = /shot (\d+) of (\d+)(?::\s*(checking))?/i.exec(progress);
  if (count || shot || /^seedance/i.test(progress)) {
    const total = Number(count?.[2] ?? shot?.[2] ?? shots);
    const done = Math.max(filmed, Number(count?.[1] ?? 0));
    const now = shot ? ` · ${shot[3] ? 'checking' : 'now on'} shot ${shot[1]}` : '';
    labels[1] = total ? `Filming · ${done} of ${total} shots filmed${now}` : 'Filming';
    return ladder(labels, 1, false);
  }
  const clip = /clip (\d+)\s*(?:of|\/)\s*(\d+)/i.exec(progress);
  if (clip) {
    // Six approved scenes usually film as one take; "clip 1 of 1" would read as a mistake.
    labels[1] =
      clip[2] === '1' ? 'Filming the scenes as one take' : `Filming clip ${clip[1]} of ${clip[2]}`;
    return ladder(labels, 1, false);
  }
  return ladder(labels, 0, false);
}

const SMALL = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
];
/** How long filming takes, from the shots: about a minute each on Seedance, never "one to three". */
export function filmEstimate(shots: number): string {
  if (shots <= 0) return 'a few minutes';
  if (shots === 1) return 'a minute or two';
  return `about ${SMALL[shots] ?? String(shots)} minutes`;
}

/** Shots still to film, read off the job's own progress line; null when the line does not say. */
export function shotsLeft(job: ReelJobDto | null, shots: number): number | null {
  if (!job) return shots;
  if (job.state === 'complete') return 0;
  const progress = job.result?.progress ?? '';
  if (!progress) return shots;
  if (/assembl|ready/i.test(progress)) return 0;
  const filmed = /(\d+) of (\d+) shots filmed/i.exec(progress);
  if (filmed) return Math.max(0, Number(filmed[2]) - Number(filmed[1]));
  return null;
}

/** The minutes filmEstimate promises for this many shots. */
const estimateMinutes = (shots: number) => (shots <= 0 ? 3 : Math.max(2, shots));
const STAY = 'It keeps going if you leave this page; come back any time.';

/**
 * The filming line: the estimate for the shots still to film, and once the first estimate has
 * passed, a plain word that it is running long and still going. No percentages, no countdown.
 */
export function filmLede(left: number, shots: number, startedAt: string, now: number): string {
  const late = now - Date.parse(startedAt) > estimateMinutes(shots) * 60_000;
  if (late) {
    const still = left > 0 ? `${left} of ${shots} shots still to film` : 'cutting it together now';
    return `This is taking longer than usual, and it keeps going: ${still}. You can leave this page; come back any time.`;
  }
  if (left <= 0) return `Cutting it together now. ${STAY}`;
  const est = filmEstimate(left).replace(/^./, (c) => c.toUpperCase());
  return `${est}${left < shots ? ' left' : ''}, a shot at a time. ${STAY}`;
}

/* ------------------------------------------------- what a paid button says about the wallet */

export function creditLine(balance: number | null, cost: number): { short: boolean; text: string } {
  if (balance === null) return { short: false, text: '' };
  if (balance < cost) return { short: true, text: `Needs ${cost} credits · you have ${balance}` };
  return { short: false, text: `Uses ${cost} of your ${balance} credits · ${balance - cost} left` };
}

/** Songs a reel may have composed, for the post's whole life (the API's cap). */
export const MUSIC_TAKES_PER_REEL = 3;
const credit = (n: number) => `${n} credit${n === 1 ? '' : 's'}`;
/** What a song costs, said where the host decides to ask for one. */
export const MUSIC_PRICE_LINE = `Each song is ${credit(CREDIT_COST.musicTrack)} · up to ${MUSIC_TAKES_PER_REEL} a reel`;
/** The price a button that composes a song carries. */
export const MUSIC_PRICE = credit(CREDIT_COST.musicTrack);

/* ------------------------------------------------- what a change to the board costs */

/**
 * "Make scene 2 warmer and scene 5 about the kitchen" names two scenes: 2 credits each. A note
 * that names none is priced at the board's ceiling, since the planner decides what changes.
 */
export function revisionQuote(
  note: string,
  sceneCount: number,
  ceiling: number,
): { scenes: number[]; credits: number; label: string } {
  const scenes = [...new Set([...note.matchAll(/scenes?\s+(\d+)/gi)].map((m) => Number(m[1])))]
    .filter((n) => n >= 1 && n <= sceneCount)
    .sort((a, b) => a - b);
  if (!scenes.length) return { scenes, credits: ceiling, label: `up to ${ceiling} credits` };
  const credits = Math.min(ceiling, scenes.length * 2);
  const list =
    scenes.length === 1
      ? `scene ${scenes[0]}`
      : `scenes ${scenes.slice(0, -1).join(', ')} and ${scenes.at(-1)}`;
  return { scenes, credits, label: `${list} · ${credits} credits` };
}

/* ------------------------------------------------- the rail */

export const RAIL = ['Moment', 'Storyboard', 'Film', 'Words', 'Music', 'Share'] as const;
const RAIL_INDEX: Record<Phase, number> = {
  moment: 0,
  building: 1,
  board: 1,
  filming: 2,
  words: 3,
  music: 4,
  mixing: 4,
  share: 5,
};

export function railFor(phase: Phase): { label: string; status: 'done' | 'now' | 'todo' }[] {
  const at = RAIL_INDEX[phase];
  return RAIL.map((label, i) => ({ label, status: i < at ? 'done' : i === at ? 'now' : 'todo' }));
}

/* ------------------------------------------------- the words on the reel */

export const WORD_SIZES = [
  { id: 'small', label: 'Small', size: 0.055 },
  { id: 'medium', label: 'Medium', size: 0.072 },
  { id: 'large', label: 'Large', size: 0.095 },
] as const;

/** Where the words start: bold on a dark pill in the lower third, the way reels that travel read. */
export function defaultWords(title: string): TextLayer {
  return {
    id: 'words',
    kind: 'text',
    text: title.trim().slice(0, 80),
    x: 0.08,
    y: 0.68,
    w: 0.84,
    rotate: 0,
    size: WORD_SIZES[1].size,
    font: 'sans',
    weight: 'bold',
    color: '#ffffff',
    align: 'center',
    pill: '#111111',
    shade: false,
  };
}

/** A 3–6 word title from the storyboard, else the first words of the caption. */
export function titleWords(title: string | null | undefined, caption: string): string {
  const t = (title ?? '').trim();
  if (t) return t;
  const first = caption.split(/[.!?\n]/)[0]?.trim() ?? '';
  return first.split(/\s+/).slice(0, 6).join(' ');
}

/** A line fits a scene when it is read in two or three seconds: about eight words. */
const LINE_MAX = 48;
function shortLine(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (t.length <= LINE_MAX) return t.replace(/[.!,;:]+$/, '');
  const cut = t.slice(0, LINE_MAX + 1);
  const atWord = cut.lastIndexOf(' ');
  return (atWord > 0 ? cut.slice(0, atWord) : cut.slice(0, LINE_MAX)).replace(/[.!,;:]+$/, '');
}

/** Words that start a trailing clause in a photo description ("pool with views through…"). */
const CLAUSE_WORDS = new Set(
  'with through and or overlooking featuring in on at of from by near under for that which where while to into over across beside next facing'.split(
    ' ',
  ),
);
const ARTICLES = new Set(['a', 'an', 'the']);
const SMALL_WORDS = new Set([...CLAUSE_WORDS, ...ARTICLES]);

/**
 * A scene's label from its title, which for a swapped-in photo is the photo's description: the
 * head phrase, at most five words, never ending on a dangling "with" or "through", in the same
 * case as the other scenes' lines. The API applies the same rule when a scene is swapped.
 */
function sceneLabel(text: string, others: string[]): string {
  const clause = text.trim().split(/[,.;:!?()]|\s[-–—]\s/)[0] ?? '';
  const words = clause.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (const w of words) {
    if (out.length === 5 || (out.length >= 2 && CLAUSE_WORDS.has(w.toLowerCase()))) break;
    out.push(w);
  }
  while (out.length > 1 && SMALL_WORDS.has(out[out.length - 1]!.toLowerCase())) out.pop();
  const label = out.join(' ');
  const styled = others.filter((o) => /[a-z]/i.test(o));
  if (styled.length && styled.every((o) => o === o.toUpperCase())) return label.toUpperCase();
  const titleCase = (o: string) =>
    o.split(/\s+/).every((w) => SMALL_WORDS.has(w.toLowerCase()) || !/^[a-z]/.test(w));
  if (styled.length && styled.every(titleCase))
    return out
      .map((w, i) =>
        i > 0 && SMALL_WORDS.has(w.toLowerCase())
          ? w.toLowerCase()
          : w[0]!.toUpperCase() + w.slice(1),
      )
      .join(' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * One line per scene from the storyboard's own captions; a scene with none is named by a short
 * label from its title, so a swapped photo never shows its cut-off description (J16).
 */
export function sceneLines(
  shots: Pick<ReelShot, 'caption' | 'title'>[],
  fallback: string,
): string[] {
  const captions = shots.map((s) => s.caption).filter(Boolean);
  return shots.map((s) =>
    s.caption
      ? shortLine(s.caption)
      : s.title
        ? sceneLabel(s.title, captions)
        : shortLine(fallback),
  );
}

export type LineTiming = { startMs: number; endMs: number };
/**
 * When each scene's line is on: the planned seconds, stretched to the film's real length (15 s
 * of scenes assembles to a touch more), so the last line ends with the reel and none is cut.
 */
export function sceneTimings(shots: Pick<ReelShot, 'seconds'>[], durationMs: number): LineTiming[] {
  const planned = shots.reduce((sum, s) => sum + Math.max(0, s.seconds), 0);
  if (!shots.length || planned <= 0 || durationMs <= 0) return [];
  const scale = durationMs / (planned * 1000);
  let at = 0;
  return shots.map((s, i) => {
    const startMs = Math.round(at);
    at += Math.max(0, s.seconds) * 1000 * scale;
    const endMs = i === shots.length - 1 ? durationMs : Math.round(at);
    return { startMs, endMs };
  });
}

/** The scene whose line is on at this moment of the film; the last one past the end. */
export function lineAt(timings: LineTiming[], ms: number): number {
  const i = timings.findIndex((t) => ms < t.endMs);
  return i === -1 ? Math.max(0, timings.length - 1) : i;
}

/** Every line in the one style the host placed; a blank line leaves its scene bare. */
export function wordsLayers(style: TextLayer, lines: string[]): TextLayer[] {
  return lines.flatMap((text, i) =>
    text.trim() ? [{ ...style, id: `words-${i + 1}`, text: text.trim() }] : [],
  );
}

/* ------------------------------------------------- the music */

export type Mood = PostTemplate['mood'][number];
/** A song per mood, then more on request; lettered within a mood so none is "take 1". */
export const SONG_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
export const MOODS: { id: Mood; label: string }[] = [
  { id: 'cozy', label: 'Cozy' },
  { id: 'calm', label: 'Calm' },
  { id: 'cinematic', label: 'Cinematic' },
  { id: 'adventurous', label: 'Upbeat' },
  { id: 'playful', label: 'Playful' },
  { id: 'romantic', label: 'Romantic' },
  { id: 'family', label: 'Family' },
  { id: 'luxury', label: 'Luxury' },
];

/** The followed post's first mood, else the quietest bed. */
export function defaultMood(template: Pick<PostTemplate, 'mood'> | null | undefined): Mood {
  return template?.mood?.[0] ?? 'cozy';
}

/* ------------------------------------------------- kept on this device */

export type ReelDraft = {
  brief: string;
  seconds: 15 | 30;
  fidelity: 'original' | 'balanced' | 'enhanced';
  postId: string | null;
};
const draftKey = (propertyId: string) => `reel-draft:${propertyId}`;
const silentKey = (postId: string) => `reel-silent:${postId}`;

export function loadReelDraft(propertyId: string): ReelDraft | null {
  try {
    const v = JSON.parse(
      localStorage.getItem(draftKey(propertyId)) ?? 'null',
    ) as Partial<ReelDraft> | null;
    if (!v || typeof v.brief !== 'string') return null;
    return {
      brief: v.brief,
      seconds: v.seconds === 30 ? 30 : 15,
      fidelity: v.fidelity === 'balanced' || v.fidelity === 'enhanced' ? v.fidelity : 'original',
      postId: typeof v.postId === 'string' ? v.postId : null,
    };
  } catch {
    return null;
  }
}

export function saveReelDraft(propertyId: string, draft: ReelDraft | null): void {
  try {
    if (draft) localStorage.setItem(draftKey(propertyId), JSON.stringify(draft));
    else localStorage.removeItem(draftKey(propertyId));
  } catch {
    /* private mode */
  }
}

/** The words the host settled on for this reel, kept on this device until the render bakes them. */
export type KeptWords = { layer: TextLayer | null; lines?: string[] };
const wordsKey = (postId: string) => `reel-words:${postId}`;

export function keptWords(postId: string): KeptWords | null {
  try {
    const raw = localStorage.getItem(wordsKey(postId));
    if (!raw) return null;
    const v = JSON.parse(raw) as { layer?: TextLayer | null; lines?: unknown };
    if (v.layer === null) return { layer: null };
    if (v.layer && v.layer.kind === 'text' && typeof v.layer.text === 'string') {
      const lines =
        Array.isArray(v.lines) && v.lines.every((l) => typeof l === 'string') ? v.lines : undefined;
      return lines ? { layer: v.layer, lines } : { layer: v.layer };
    }
    return null;
  } catch {
    return null;
  }
}

export function keepWords(postId: string, words: KeptWords | null): void {
  try {
    if (words) localStorage.setItem(wordsKey(postId), JSON.stringify(words));
    else localStorage.removeItem(wordsKey(postId));
  } catch {
    /* private mode */
  }
}

export function keptSilent(postId: string): boolean {
  try {
    return localStorage.getItem(silentKey(postId)) === '1';
  } catch {
    return false;
  }
}

export function keepSilent(postId: string, silent: boolean): void {
  try {
    if (silent) localStorage.setItem(silentKey(postId), '1');
    else localStorage.removeItem(silentKey(postId));
  } catch {
    /* private mode */
  }
}
